/**
 * Lead Details Page - Enhanced Edition
 * 360° view of a single lead with full interaction history
 * Features: Quick-edit fields, bulk updates, communication log, follow-up scheduler, field history
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  VStack,
  Badge,
  useToast,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useDisclosure,
  Input,
  List,
  ListItem,
  ListIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Editable,
  EditableInput,
  EditablePreview,
  EditableTextarea,
  useEditableControls,
  ButtonGroup,
  Code,
} from '@chakra-ui/react';
import {
  MdArrowBack,
  MdEmail,
  MdPhone,
  MdBusiness,
  MdPerson,
  MdCalendarToday,
  MdEdit,
  MdSchedule,
  MdDescription,
  MdCheckCircle,
  MdCancel,
  MdNote,
  MdCall,
  MdEvent,
  MdCheck,
  MdClose,
  MdHistory,
} from 'react-icons/md';
import { useAuth } from '../../../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import {
  getLead,
  updateLeadStatus,
  addLeadActivity,
  scheduleDemoForLead,
  closeLeadAsWon,
  closeLeadAsLost,
  bulkUpdateLead,
  getFieldChangeHistory,
  setNextFollowupDate,
  assignLead,
} from '../../../services/leadManagement';
import {
  Lead,
  LeadStatus,
  ActivityType,
  getLeadStatusDisplay,
  getLeadStatusColor,
} from '../../../types/lead';

function EditableControls() {
  const { isEditing, getSubmitButtonProps, getCancelButtonProps } =
    useEditableControls();

  return isEditing ? (
    <ButtonGroup size="sm" ml="2">
      <IconButton
        aria-label="confirm"
        icon={<MdCheck />}
        {...getSubmitButtonProps()}
      />
      <IconButton
        aria-label="cancel"
        icon={<MdClose />}
        {...getCancelButtonProps()}
      />
    </ButtonGroup>
  ) : null;
}

export function LeadDetails() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { userProfile } = useAuth();

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [fieldHistory, setFieldHistory] = useState<any[]>([]);

  // Modals
  const { isOpen: isStatusModalOpen, onOpen: onStatusModalOpen, onClose: onStatusModalClose } = useDisclosure();
  const { isOpen: isDemoModalOpen, onOpen: onDemoModalOpen, onClose: onDemoModalClose } = useDisclosure();
  const { isOpen: isActivityModalOpen, onOpen: onActivityModalOpen, onClose: onActivityModalClose } = useDisclosure();
  const { isOpen: isCloseModalOpen, onOpen: onCloseModalOpen, onClose: onCloseModalClose } = useDisclosure();
  const { isOpen: isBulkUpdateModalOpen, onOpen: onBulkUpdateModalOpen, onClose: onBulkUpdateModalClose } = useDisclosure();
  const { isOpen: isFollowupModalOpen, onOpen: onFollowupModalOpen, onClose: onFollowupModalClose } = useDisclosure();
  const { isOpen: isAssignModalOpen, onOpen: onAssignModalOpen, onClose: onAssignModalClose } = useDisclosure();

  // Form states
  const [newStatus, setNewStatus] = useState<LeadStatus>('new');
  const [statusNotes, setStatusNotes] = useState('');
  const [demoDate, setDemoDate] = useState('');
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [activitySubject, setActivitySubject] = useState('');
  const [activityDescription, setActivityDescription] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [closeAsWon, setCloseAsWon] = useState(true);
  const [followupDays, setFollowupDays] = useState('3');

  // Bulk update form states
  const [bulkForm, setBulkForm] = useState({
    doorCount: '',
    timeline: '',
    currentSolution: '',
    painPoints: '',
    phone: '',
    company: '',
    position: '',
  });

  // Assignment form states
  const [assignToUserId, setAssignToUserId] = useState('');
  const [assignToUserName, setAssignToUserName] = useState('');
  const [availableSalesReps, setAvailableSalesReps] = useState<{id: string; name: string; email: string}[]>([]);

  // Load lead and history
  const loadLead = async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const data = await getLead(leadId);
      setLead(data);
      
      // Load field change history
      const history = await getFieldChangeHistory(leadId);
      setFieldHistory(history);

      // Update bulk form with current values
      if (data) {
        setBulkForm({
          doorCount: data.businessInfo?.doorCount?.toString() || '',
          timeline: data.businessInfo?.timeline || '',
          currentSolution: data.businessInfo?.currentSolution || '',
          painPoints: Array.isArray(data.businessInfo?.painPoints)
            ? data.businessInfo.painPoints.join('\n')
            : data.businessInfo?.painPoints || '',
          phone: data.contactInfo?.phone || '',
          company: data.contactInfo?.company || '',
          position: data.contactInfo?.position || '',
        });
      }
    } catch (error) {
      toast({
        title: 'Error loading lead',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLead();
  }, [leadId]);

  const handleStatusChange = async () => {
    if (!lead || !userProfile || !userProfile.name) return;
    try {
      await updateLeadStatus(lead.id, newStatus, userProfile.uid, userProfile.name, statusNotes);
      toast({ title: 'Status updated', status: 'success', duration: 3000 });
      onStatusModalClose();
      loadLead();
    } catch (error) {
      toast({
        title: 'Error updating status',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleScheduleDemo = async () => {
    if (!lead || !userProfile || !userProfile.name || !demoDate) return;
    try {
      await scheduleDemoForLead(lead.id, Timestamp.fromDate(new Date(demoDate)), userProfile.uid, userProfile.name);
      toast({ title: 'Demo scheduled', status: 'success', duration: 3000 });
      onDemoModalClose();
      loadLead();
    } catch (error) {
      toast({
        title: 'Error scheduling demo',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleAddActivity = async () => {
    if (!lead || !userProfile || !userProfile.name || !activitySubject) return;
    try {
      await addLeadActivity(lead.id, activityType, activitySubject, activityDescription, userProfile.uid, userProfile.name);
      toast({ title: 'Activity added', status: 'success', duration: 3000 });
      setActivitySubject('');
      setActivityDescription('');
      onActivityModalClose();
      loadLead();
    } catch (error) {
      toast({
        title: 'Error adding activity',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleCloseLead = async () => {
    if (!lead || !userProfile || !userProfile.name) return;
    try {
      if (closeAsWon) {
        await closeLeadAsWon(lead.id, '', lead.estimatedValue, userProfile.uid, userProfile.name);
      } else {
        await closeLeadAsLost(lead.id, closeReason, userProfile.uid, userProfile.name);
      }
      toast({
        title: `Lead marked as ${closeAsWon ? 'won' : 'lost'}`,
        status: closeAsWon ? 'success' : 'info',
        duration: 3000,
      });
      onCloseModalClose();
      loadLead();
    } catch (error) {
      toast({
        title: 'Error closing lead',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleBulkUpdate = async () => {
    if (!lead || !userProfile) return;
    try {
      const updates: any = {
        businessInfo: {
          doorCount: bulkForm.doorCount ? parseInt(bulkForm.doorCount) : lead.businessInfo?.doorCount,
          timeline: bulkForm.timeline || undefined,
          currentSolution: bulkForm.currentSolution || undefined,
          painPoints: bulkForm.painPoints
            ? bulkForm.painPoints.split('\n').filter(p => p.trim())
            : lead.businessInfo?.painPoints || [],
        },
        contactInfo: {
          phone: bulkForm.phone || undefined,
          company: bulkForm.company || undefined,
          position: bulkForm.position || undefined,
        },
      };

      await bulkUpdateLead(lead.id, updates, userProfile.uid);
      toast({ title: 'Lead updated successfully', status: 'success', duration: 3000 });
      onBulkUpdateModalClose();
      loadLead();
    } catch (error) {
      toast({
        title: 'Error updating lead',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleAssign = async () => {
    if (!lead || !userProfile || !assignToUserId || !assignToUserName) return;
    try {
      await assignLead(lead.id, assignToUserId, assignToUserName, userProfile.uid);
      toast({ title: `Lead assigned to ${assignToUserName}`, status: 'success', duration: 3000 });
      onAssignModalClose();
      setAssignToUserId('');
      setAssignToUserName('');
      loadLead();
    } catch (error) {
      toast({
        title: 'Error assigning lead',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleSetFollowup = async () => {
    if (!lead || !userProfile) return;
    try {
      await setNextFollowupDate(lead.id, parseInt(followupDays));
      await addLeadActivity(lead.id, 'note', 'Follow-up Scheduled', `Follow-up scheduled for ${followupDays} days from now`, userProfile.uid, userProfile.name || 'Unknown');
      toast({ title: 'Follow-up scheduled', status: 'success', duration: 3000 });
      onFollowupModalClose();
      loadLead();
    } catch (error) {
      toast({
        title: 'Error scheduling follow-up',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleQuickEdit = async (field: string, value: any) => {
    if (!lead || !userProfile) return;
    try {
      let updates: any = {};
      
      if (field.startsWith('business_')) {
        const businessField = field.replace('business_', '');
        updates.businessInfo = { ...lead.businessInfo, [businessField]: value };
      } else if (field.startsWith('contact_')) {
        const contactField = field.replace('contact_', '');
        updates.contactInfo = { ...lead.contactInfo, [contactField]: value };
      }

      await bulkUpdateLead(lead.id, updates, userProfile.uid);
      toast({ title: 'Updated', status: 'success', duration: 2000 });
      loadLead();
    } catch (error) {
      toast({
        title: 'Error updating',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(value);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'call': return MdCall;
      case 'email': return MdEmail;
      case 'demo': return MdEvent;
      case 'proposal': return MdDescription;
      case 'meeting': return MdCalendarToday;
      case 'note': return MdNote;
      default: return MdNote;
    }
  };

  if (loading) {
    return (<Flex justify="center" align="center" h="400px"><Spinner size="xl" /></Flex>);
  }

  if (!lead) {
    return (<Box pt={{ base: '130px', md: '80px', xl: '80px' }}><Text>Lead not found</Text></Box>);
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb="6" flexWrap="wrap" gap="2">
        <HStack>
          <IconButton aria-label="Back" icon={<MdArrowBack />} onClick={() => navigate('/admin/sales/leads')} />
          <Heading size="lg">{lead.contactInfo?.name || 'Unknown Lead'}</Heading>
          <Badge colorScheme={getLeadStatusColor(lead.status)} fontSize="md">{getLeadStatusDisplay(lead.status)}</Badge>
        </HStack>
        <HStack flexWrap="wrap">
          <Button leftIcon={<MdNote />} onClick={onActivityModalOpen} size="sm">Add Note</Button>
          <Button leftIcon={<MdEdit />} onClick={onBulkUpdateModalOpen} size="sm">Bulk Update</Button>
          <Button leftIcon={<MdSchedule />} onClick={onFollowupModalOpen} size="sm">Follow-up</Button>
          <Button leftIcon={<MdPerson />} onClick={onAssignModalOpen} colorScheme="blue" size="sm">Assign To</Button>
          <Button leftIcon={<MdDescription />} colorScheme="purple" onClick={() => navigate(`/admin/sales/proposals/new?leadId=${lead.id}`)} isDisabled={['closed_won', 'closed_lost'].includes(lead.status)} size="sm">Proposal</Button>
          <Button leftIcon={<MdEvent />} onClick={onDemoModalOpen} isDisabled={['closed_won', 'closed_lost'].includes(lead.status)} size="sm">Demo</Button>
          <Button leftIcon={<MdEdit />} onClick={onStatusModalOpen} isDisabled={['closed_won', 'closed_lost'].includes(lead.status)} size="sm">Status</Button>
          <Button leftIcon={lead.status === 'closed_won' ? <MdCheckCircle /> : <MdCancel />} colorScheme={lead.status === 'closed_won' ? 'green' : 'red'} onClick={() => { setCloseAsWon(lead.status !== 'closed_won'); onCloseModalOpen(); }} isDisabled={['closed_won', 'closed_lost'].includes(lead.status)} size="sm">Close</Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 4 }} gap="4" mb="6">
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Lead Score</StatLabel>
              <StatNumber>{lead.score}/100</StatNumber>
              <StatHelpText>{lead.score >= 70 ? 'Hot' : lead.score >= 40 ? 'Warm' : 'Cold'}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Estimated Value</StatLabel>
              <StatNumber>{formatCurrency(lead.estimatedValue)}</StatNumber>
              <StatHelpText>{lead.probability}% probability</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Door Count</StatLabel>
              <StatNumber>{lead.businessInfo?.doorCount || 'N/A'}</StatNumber>
              <StatHelpText>Tier: {lead.businessInfo?.pricingTier || 'N/A'}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Source</StatLabel>
              <StatNumber fontSize="lg" textTransform="capitalize">{lead.source.replace('_', ' ')}</StatNumber>
              <StatHelpText>Created {formatDate(lead.createdAt)}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Main Content with Tabs */}
      <Tabs>
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Communications</Tab>
          <Tab>Change History</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel>
            <Grid templateColumns="repeat(12, 1fr)" gap="6">
              {/* Left Column - Contact & Business Info */}
              <GridItem colSpan={{ base: 12, lg: 4 }}>
                <Stack spacing="4">
                  {/* Contact Information */}
                  <Card>
                    <CardHeader>
                      <Flex justify="space-between" align="center">
                        <Heading size="md">Contact Information</Heading>
                        <Icon as={MdEdit} color="gray.400" fontSize="sm" />
                      </Flex>
                    </CardHeader>
                    <CardBody>
                      <VStack align="start" spacing="4">
                        <HStack width="full">
                          <Icon as={MdPerson} color="gray.500" />
                          <Text fontWeight="semibold" minW="80px">Name:</Text>
                          <Text>{lead.contactInfo?.name || 'N/A'}</Text>
                        </HStack>
                        <HStack width="full">
                          <Icon as={MdEmail} color="gray.500" />
                          <Text fontWeight="semibold" minW="80px">Email:</Text>
                          <Text>{lead.contactInfo?.email || 'N/A'}</Text>
                        </HStack>
                        <Box width="full">
                          <HStack mb="2">
                            <Icon as={MdPhone} color="gray.500" />
                            <Text fontWeight="semibold">Phone:</Text>
                          </HStack>
                          <Editable defaultValue={lead.contactInfo?.phone || ''} onSubmit={(value) => handleQuickEdit('contact_phone', value)}>
                            <EditablePreview pl="2" />
                            <EditableInput />
                            <EditableControls />
                          </Editable>
                        </Box>
                        <Box width="full">
                          <HStack mb="2">
                            <Icon as={MdBusiness} color="gray.500" />
                            <Text fontWeight="semibold">Company:</Text>
                          </HStack>
                          <Editable defaultValue={lead.contactInfo?.company || ''} onSubmit={(value) => handleQuickEdit('contact_company', value)}>
                            <EditablePreview pl="2" />
                            <EditableInput />
                            <EditableControls />
                          </Editable>
                        </Box>
                        <Box width="full">
                          <HStack mb="2">
                            <Icon as={MdPerson} color="gray.500" />
                            <Text fontWeight="semibold">Position:</Text>
                          </HStack>
                          <Editable defaultValue={lead.contactInfo?.position || ''} onSubmit={(value) => handleQuickEdit('contact_position', value)}>
                            <EditablePreview pl="2" />
                            <EditableInput />
                            <EditableControls />
                          </Editable>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Business Information */}
                  <Card>
                    <CardHeader>
                      <Flex justify="space-between" align="center">
                        <Heading size="md">Business Information</Heading>
                        <Icon as={MdEdit} color="gray.400" fontSize="sm" />
                      </Flex>
                    </CardHeader>
                    <CardBody>
                      <VStack align="start" spacing="4">
                        <Box width="full">
                          <Text fontWeight="semibold" mb="2">Door Count:</Text>
                          <Editable defaultValue={(lead.businessInfo?.doorCount || '').toString()} onSubmit={(value) => handleQuickEdit('business_doorCount', parseInt(value))}>
                            <EditablePreview />
                            <EditableInput type="number" />
                            <EditableControls />
                          </Editable>
                          <Text fontSize="sm" color="gray.500">Tier: {lead.businessInfo?.pricingTier || 'N/A'}</Text>
                        </Box>
                        <Box width="full">
                          <Text fontWeight="semibold" mb="2">Location:</Text>
                          <Text>{lead.businessInfo?.city || 'Not set'}, {lead.businessInfo?.province || 'Not set'}</Text>
                        </Box>
                        <Box width="full">
                          <Text fontWeight="semibold" mb="2">Current Solution:</Text>
                          <Editable defaultValue={lead.businessInfo?.currentSolution || ''} onSubmit={(value) => handleQuickEdit('business_currentSolution', value)}>
                            <EditablePreview pl="2" />
                            <EditableInput />
                            <EditableControls />
                          </Editable>
                        </Box>
                        <Box width="full">
                          <Text fontWeight="semibold" mb="2">Timeline:</Text>
                          <Editable defaultValue={lead.businessInfo?.timeline || ''} onSubmit={(value) => handleQuickEdit('business_timeline', value)}>
                            <EditablePreview pl="2" />
                            <EditableInput />
                            <EditableControls />
                          </Editable>
                        </Box>
                        <Box width="full">
                          <Text fontWeight="semibold" mb="2">Budget:</Text>
                          <Text>{lead.businessInfo?.budget || 'Not specified'}</Text>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Pain Points */}
                  {lead.businessInfo?.painPoints && (
                    <Card>
                      <CardHeader>
                        <Heading size="md">Pain Points</Heading>
                      </CardHeader>
                      <CardBody>
                        {Array.isArray(lead.businessInfo?.painPoints) ? (
                          <List spacing="2">
                            {lead.businessInfo.painPoints.map((point, index) => (
                              <ListItem key={index}>
                                <ListIcon as={MdCheckCircle} color="green.500" />
                                {point}
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Text>{lead.businessInfo.painPoints}</Text>
                        )}
                      </CardBody>
                    </Card>
                  )}
                </Stack>
              </GridItem>

              {/* Right Column - Activity Timeline */}
              <GridItem colSpan={{ base: 12, lg: 8 }}>
                <Card>
                  <CardHeader>
                    <Heading size="md">Activity Timeline</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing="4">
                      {lead.activities && lead.activities.length > 0 ? (
                        lead.activities
                          .filter((a: any) => a && a.timestamp)
                          .sort((a: any, b: any) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0))
                          .map((activity, index) => (
                            <Box key={index} p="4" borderLeft="3px solid" borderColor="brand.500" bg="gray.50" borderRadius="md">
                              <HStack justify="space-between" mb="2">
                                <HStack>
                                  <Icon as={getActivityIcon(activity.type)} color="brand.500" />
                                  <Text fontWeight="semibold">{activity.subject}</Text>
                                  <Badge>{activity.type}</Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.500">{formatDate(activity.timestamp)}</Text>
                              </HStack>
                              {activity.description && <Text color="gray.700">{activity.description}</Text>}
                              <Text fontSize="sm" color="gray.500" mt="2">by {activity.performedByName}</Text>
                            </Box>
                          ))
                      ) : (
                        <Text color="gray.500" textAlign="center" py="8">No activity recorded yet</Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </GridItem>
            </Grid>
          </TabPanel>

          {/* Communications Tab */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Communication Log</Heading>
              </CardHeader>
              <CardBody>
                <Tabs variant="soft-rounded">
                  <TabList mb="1em">
                    <Tab>All</Tab>
                    <Tab>Emails</Tab>
                    <Tab>Calls</Tab>
                    <Tab>Meetings</Tab>
                  </TabList>

                  <TabPanels>
                    {/* All Communications */}
                    <TabPanel>
                      <VStack align="stretch" spacing="3">
                        {lead.activities && lead.activities.filter((a: any) => a && ['email', 'call', 'meeting', 'demo'].includes(a.type)).length > 0 ? (
                          lead.activities
                            .filter((a: any) => a && ['email', 'call', 'meeting', 'demo'].includes(a.type))
                            .sort((a: any, b: any) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0))
                            .map((activity, idx) => (
                              <Box key={idx} p="3" border="1px" borderColor="gray.200" borderRadius="md">
                                <HStack justify="space-between" mb="2">
                                  <HStack>
                                    <Icon as={getActivityIcon(activity.type)} />
                                    <Text fontWeight="semibold">{activity.subject}</Text>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.500">{formatDate(activity.timestamp)}</Text>
                                </HStack>
                                <Text color="gray.700" fontSize="sm">{activity.description}</Text>
                              </Box>
                            ))
                        ) : (
                          <Text color="gray.500">No communications recorded</Text>
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Emails */}
                    <TabPanel>
                      <VStack align="stretch" spacing="3">
                        {lead.emailHistory && lead.emailHistory.length > 0 ? (
                          lead.emailHistory
                            .sort((a: any, b: any) => (b.sentAt?.toMillis?.() || 0) - (a.sentAt?.toMillis?.() || 0))
                            .map((email, idx) => (
                              <Box key={idx} p="3" border="1px" borderColor="gray.200" borderRadius="md" bg={email.generatedByAI ? 'blue.50' : 'white'}>
                                <HStack justify="space-between" mb="2">
                                  <HStack>
                                    <Icon as={MdEmail} />
                                    <Text fontWeight="semibold">{email.subject}</Text>
                                    {email.generatedByAI && <Badge colorScheme="blue">AI</Badge>}
                                  </HStack>
                                  <Text fontSize="sm" color="gray.500">{formatDate(email.sentAt)}</Text>
                                </HStack>
                                <Text color="gray.700" fontSize="sm" whiteSpace="pre-wrap">{email.body}</Text>
                                <Text fontSize="xs" color="gray.500" mt="2">Sent by: {email.sentByName}</Text>
                              </Box>
                            ))
                        ) : (
                          <Text color="gray.500">No emails sent yet</Text>
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Calls */}
                    <TabPanel>
                      <VStack align="stretch" spacing="3">
                        {lead.activities && lead.activities.filter((a: any) => a?.type === 'call').length > 0 ? (
                          lead.activities
                            .filter((a: any) => a?.type === 'call')
                            .sort((a: any, b: any) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0))
                            .map((activity, idx) => (
                              <Box key={idx} p="3" border="1px" borderColor="gray.200" borderRadius="md">
                                <HStack justify="space-between" mb="2">
                                  <HStack>
                                    <Icon as={MdCall} />
                                    <Text fontWeight="semibold">{activity.subject}</Text>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.500">{formatDate(activity.timestamp)}</Text>
                                </HStack>
                                <Text color="gray.700" fontSize="sm">{activity.description}</Text>
                              </Box>
                            ))
                        ) : (
                          <Text color="gray.500">No calls recorded</Text>
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Meetings */}
                    <TabPanel>
                      <VStack align="stretch" spacing="3">
                        {lead.activities && lead.activities.filter((a: any) => a?.type === 'meeting').length > 0 ? (
                          lead.activities
                            .filter((a: any) => a?.type === 'meeting')
                            .sort((a: any, b: any) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0))
                            .map((activity, idx) => (
                              <Box key={idx} p="3" border="1px" borderColor="gray.200" borderRadius="md">
                                <HStack justify="space-between" mb="2">
                                  <HStack>
                                    <Icon as={MdCalendarToday} />
                                    <Text fontWeight="semibold">{activity.subject}</Text>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.500">{formatDate(activity.timestamp)}</Text>
                                </HStack>
                                <Text color="gray.700" fontSize="sm">{activity.description}</Text>
                              </Box>
                            ))
                        ) : (
                          <Text color="gray.500">No meetings recorded</Text>
                        )}
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Change History Tab */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Field Change History</Heading>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing="3">
                  {fieldHistory && fieldHistory.length > 0 ? (
                    fieldHistory
                      .sort((a: any, b: any) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0))
                      .map((entry, idx) => (
                        <Box key={idx} p="3" border="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
                          <HStack justify="space-between" mb="2">
                            <HStack>
                              <Icon as={MdHistory} />
                              <Text fontWeight="semibold">{entry.action}</Text>
                            </HStack>
                            <Text fontSize="sm" color="gray.500">{formatDate(entry.timestamp)}</Text>
                          </HStack>
                          <Text color="gray.700" fontSize="sm" mb="2">{entry.details?.name || ''}</Text>
                          <Box bg="white" p="2" borderRadius="sm" fontSize="xs" overflowX="auto">
                            <Code>{JSON.stringify(entry.details, null, 2)}</Code>
                          </Box>
                          <Text fontSize="xs" color="gray.500" mt="2">By: {entry.performedBy}</Text>
                        </Box>
                      ))
                  ) : (
                    <Text color="gray.500">No change history available</Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Status Update Modal */}
      <Modal isOpen={isStatusModalOpen} onClose={onStatusModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Lead Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <FormControl isRequired>
                <FormLabel>New Status</FormLabel>
                <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value as LeadStatus)}>
                  <option value="new">New Lead</option>
                  <option value="qualifying">Qualifying</option>
                  <option value="demo_scheduled">Demo Scheduled</option>
                  <option value="demo_completed">Demo Completed</option>
                  <option value="proposal_sent">Proposal Sent</option>
                  <option value="negotiating">Negotiating</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} placeholder="Add any notes about this status change..." />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onStatusModalClose} mr={3}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleStatusChange}>Update Status</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Schedule Demo Modal */}
      <Modal isOpen={isDemoModalOpen} onClose={onDemoModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Schedule Demo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Demo Date & Time</FormLabel>
              <Input type="datetime-local" value={demoDate} onChange={(e) => setDemoDate(e.target.value)} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDemoModalClose} mr={3}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleScheduleDemo}>Schedule Demo</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Activity Modal */}
      <Modal isOpen={isActivityModalOpen} onClose={onActivityModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Activity</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <FormControl isRequired>
                <FormLabel>Activity Type</FormLabel>
                <Select value={activityType} onChange={(e) => setActivityType(e.target.value as ActivityType)}>
                  <option value="note">Note</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Subject</FormLabel>
                <Input value={activitySubject} onChange={(e) => setActivitySubject(e.target.value)} placeholder="Brief description..." />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea value={activityDescription} onChange={(e) => setActivityDescription(e.target.value)} placeholder="Detailed notes..." />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onActivityModalClose} mr={3}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleAddActivity}>Add Activity</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Close Lead Modal */}
      <Modal isOpen={isCloseModalOpen} onClose={onCloseModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Close Lead as {closeAsWon ? 'Won' : 'Lost'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {closeAsWon ? (
              <Text>Mark this lead as won? The estimated value is <strong>{formatCurrency(lead.estimatedValue)}</strong>.</Text>
            ) : (
              <FormControl isRequired>
                <FormLabel>Reason for Loss</FormLabel>
                <Textarea value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="Why did we lose this deal?" />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseModalClose} mr={3}>Cancel</Button>
            <Button colorScheme={closeAsWon ? 'green' : 'red'} onClick={handleCloseLead}>Confirm</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal isOpen={isBulkUpdateModalOpen} onClose={onBulkUpdateModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Bulk Update Lead Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Tabs>
              <TabList mb="1em">
                <Tab>Contact</Tab>
                <Tab>Business</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <Stack spacing="4">
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input value={bulkForm.phone} onChange={(e) => setBulkForm({ ...bulkForm, phone: e.target.value })} placeholder="Leave blank if no change" />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Company</FormLabel>
                      <Input value={bulkForm.company} onChange={(e) => setBulkForm({ ...bulkForm, company: e.target.value })} placeholder="Leave blank if no change" />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Position</FormLabel>
                      <Input value={bulkForm.position} onChange={(e) => setBulkForm({ ...bulkForm, position: e.target.value })} placeholder="Leave blank if no change" />
                    </FormControl>
                  </Stack>
                </TabPanel>

                <TabPanel>
                  <Stack spacing="4">
                    <FormControl>
                      <FormLabel>Door Count</FormLabel>
                      <Input type="number" value={bulkForm.doorCount} onChange={(e) => setBulkForm({ ...bulkForm, doorCount: e.target.value })} placeholder="Leave blank if no change" />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Current Solution</FormLabel>
                      <Input value={bulkForm.currentSolution} onChange={(e) => setBulkForm({ ...bulkForm, currentSolution: e.target.value })} placeholder="Leave blank if no change" />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Timeline</FormLabel>
                      <Input value={bulkForm.timeline} onChange={(e) => setBulkForm({ ...bulkForm, timeline: e.target.value })} placeholder="Leave blank if no change" />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Pain Points (one per line)</FormLabel>
                      <Textarea value={bulkForm.painPoints} onChange={(e) => setBulkForm({ ...bulkForm, painPoints: e.target.value })} placeholder="Leave blank if no change" rows={5} />
                    </FormControl>
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onBulkUpdateModalClose} mr={3}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleBulkUpdate}>Update Lead</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Follow-up Scheduling Modal */}
      <Modal isOpen={isFollowupModalOpen} onClose={onFollowupModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Schedule Follow-up</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Follow-up in N days</FormLabel>
              <Input type="number" value={followupDays} onChange={(e) => setFollowupDays(e.target.value)} placeholder="Days from today" />
              <Text fontSize="sm" color="gray.500" mt="2">A note will be added to the activity timeline and an audit log entry will be created.</Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onFollowupModalClose} mr={3}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSetFollowup}>Schedule Follow-up</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assign Lead Modal */}
      <Modal isOpen={isAssignModalOpen} onClose={onAssignModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Lead to Sales Rep</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Currently Assigned To</FormLabel>
                <Text fontWeight="bold" color="gray.600">
                  {typeof lead?.assignedTo === 'string' ? lead.assignedTo : (lead?.assignedTo as any)?.name || 'Unassigned'}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel>Assign To</FormLabel>
                <Select 
                  value={assignToUserId} 
                  onChange={(e) => {
                    const selected = e.target.value;
                    const salesReps = [
                      { id: 'test-sales-1', name: 'Test Sales Rep 1', email: 'test-sales@dossiloc.com' },
                      { id: 'test-sales-2', name: 'Test Sales Rep 2', email: 'test-sales2@dossiloc.com' },
                      { id: 'test-admin@dossiloc.com', name: 'Test Admin', email: 'test-admin@dossiloc.com' },
                      { id: 'test-support@dossiloc.com', name: 'Test Support', email: 'test-support@dossiloc.com' },
                    ];
                    const rep = salesReps.find(r => r.id === selected);
                    if (rep) {
                      setAssignToUserId(rep.id);
                      setAssignToUserName(rep.name);
                    }
                  }}
                  placeholder="Select a sales representative"
                >
                  <option value="test-sales-1">Test Sales Rep 1 (test-sales@dossiloc.com)</option>
                  <option value="test-sales-2">Test Sales Rep 2 (test-sales2@dossiloc.com)</option>
                  <option value="test-admin@dossiloc.com">Test Admin (test-admin@dossiloc.com)</option>
                  <option value="test-support@dossiloc.com">Test Support (test-support@dossiloc.com)</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Assignment Notes</FormLabel>
                <Textarea 
                  placeholder="Add any notes about this assignment (optional)"
                  rows={3}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onAssignModalClose} mr={3}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleAssign} isDisabled={!assignToUserId}>
              Assign Lead
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
